import { ensureTopic, getTopic, type TopicRecord } from './topicStore';
import type { TopicChapterListItem } from './apiContracts';
import type { Root } from './prototypeTypes';
import { getRuntime } from './runtimeAccessor';
import { presentMediaAccessDecision, resolveMediaAccessDecision } from './mediaPlayerAccessGate';
import { topicCategoryLabels } from '../../shared/topicCategoryLabels';
import { topicPosterSurfaceMarkup } from '../../shared/topicPosterContent';
import { applyTopicCover } from './topicCoverLoader';
import { categoriesFromClassification, coverUrlForTopic } from '../../domain/topicCover';
import { topicFavoriteVersion, topicVoteVersion } from './topicFavoriteVersion';
import { getTopicNavigationPreview } from './topicNavigationPreview';

/**
 * Now-playing panel painter: renders the topic header (title + subtitle), the
 * hero cover backdrop and the chapter playlist. Pure presentation - chapter
 * navigation is delegated through the `deepmox:audio-goto` event so playback
 * policy stays inside the audio controls module.
 *
 * Hierarchy follows the topic landing page: the TOPIC is the main title and
 * its subtitle the secondary line, with the chapter list below. The previous
 * layout promoted the chapter title, which inverted that relationship.
 */

interface ActiveChapterStamp {
	index?: number;
	title?: string;
	topicTitle?: string;
	topicSlug?: string;
	topicSubtitle?: string;
}

type PanelTopic = Pick<TopicRecord, 'title' | 'subtitle' | 'category' | 'classification' | 'coverImageUrl' | 'coverGradient' | 'previewChapters' | 'lockedChapters' | 'audioMinutes' | 'engagement' | 'source'> & {
	coverBadge?: 'github' | 'new' | 'trending';
};

const LOCK_ICON_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>';

const playlistCache = new Map<string, TopicChapterListItem[]>();
const clickBoundRoots = new WeakSet<HTMLElement>();
const reactionLoadSequence = new WeakMap<object, number>();

export function paintNowPlayingPanel(root: Root): void {
	const active = readActiveChapter(root);
	const cached = active?.topicSlug ? getTopic(active.topicSlug) : undefined;
	const preview = active?.topicSlug ? getTopicNavigationPreview(root, active.topicSlug) : undefined;
	const topic = active?.topicSlug ? preserveNavigationPreview(resolveNowPlayingTopic(root, active.topicSlug, cached), preview) : undefined;
	paintTopicCard(root, active, topic);
	if (!hasPresentationFields(cached) && active?.topicSlug) {
		// Cold or invalidated topicStore (direct chapter deep link): the matching
		// SSR DTO can paint immediately, while the matching navigation projection
		// warms for later navigation and takes precedence once complete.
		void ensureTopic(active.topicSlug!)
			.then(() => {
				if (readActiveChapter(root)?.topicSlug !== active.topicSlug) return;
				const warmed = getTopic(active.topicSlug!);
				if (!hasPresentationFields(warmed)) return;
				paintTopicCard(root, active, preserveNavigationPreview(resolveNowPlayingTopic(root, active.topicSlug!, warmed), getTopicNavigationPreview(root, active.topicSlug!)));
			})
			.catch(() => undefined);
	}
	void renderPlaylist(root, active);
}

/**
 * A direct Topic/Chapter page already includes a server-validated DTO. Auth
 * hydration deliberately invalidates the client marketing index, which can
 * race the first audio paint and otherwise leave Now Playing on its generic
 * cover. Only use that DTO when it names the media's exact topic identity:
 * playback from Topic A while the user is browsing Topic B must never borrow
 * B's poster or category. A complete navigation record remains the primary source.
 */
export function resolveNowPlayingTopic(root: Root, topicIdentity: string, cached: PanelTopic | undefined = getTopic(topicIdentity)): PanelTopic | undefined {
	if (hasPresentationFields(cached)) return cached;
	// Home's card projection is valid only for this root and exact slug. It is
	// the complete first-paint poster when the auth transition has temporarily
	// invalidated topicStore. Never consult it for a different playing topic.
	const navigationPreview = getTopicNavigationPreview(root, topicIdentity);
	if (navigationPreview) return navigationPreview;
	const pageTopic = matchingPageModelTopic(root, topicIdentity);
	return pageTopic ?? cached;
}

export function matchingPageModelTopic(root: Root, topicIdentity: string): PanelTopic | undefined {
	const serialized = root.querySelector('#page-model')?.textContent ?? root.ownerDocument?.querySelector('#page-model')?.textContent;
	if (!serialized) return undefined;
	try {
		const model = JSON.parse(serialized) as Record<string, unknown>;
		const topic = record(model.topic);
		if (!topic) return undefined;
		const slug = text(topic.slug);
		const id = text(topic.id);
		if (topicIdentity !== slug && topicIdentity !== id) return undefined;
		const rawChapters = model.pageType === 'topic' ? model.chapters : model.directory;
		const chapters: unknown[] = Array.isArray(rawChapters) ? rawChapters : [];
		const previewChapters: TopicRecord['previewChapters'] = chapters.flatMap((chapter) => {
			const item = record(chapter);
			const index = number(item?.index);
			const title = text(item?.title);
			if (!item || !index || !title) return [];
			return [{ id: text(item.id) ?? `${slug}-${index}`, index, title, duration: text(item.duration) ?? '', hasAudio: item.hasAudio === true, hasVideo: item.hasVideo === true }];
		});
		return {
			title: text(topic.title) ?? 'DeepMox',
			subtitle: text(topic.subtitle) ?? '',
			category: text(topic.category) ?? '',
			classification: record(topic.classification) as PanelTopic['classification'],
			coverImageUrl: text(topic.coverImageUrl),
			coverGradient: '',
			source: text(topic.source) as PanelTopic['source'],
			coverBadge: coverBadge(topic.coverBadge),
			previewChapters,
			lockedChapters: [],
			audioMinutes: previewChapters.reduce((total, chapter) => total + Number(chapter.duration.match(/\d+/u)?.[0] ?? 0), 0),
		};
	} catch {
		return undefined;
	}
}

export function resolveNowPlayingTopicText(active: Pick<ActiveChapterStamp, 'topicTitle' | 'topicSubtitle'> | undefined, topic: Pick<PanelTopic, 'title' | 'subtitle'> | undefined): { title: string; subtitle: string } {
	return {
		title: topic?.title || active?.topicTitle || 'DeepMox',
		subtitle: topic?.subtitle || active?.topicSubtitle || '',
	};
}

function hasPresentationFields(topic: PanelTopic | undefined): topic is PanelTopic {
	const hasCategory = Boolean(topic?.category.trim() || topic?.classification?.primaryCategory?.label?.trim());
	return Boolean(topic?.coverImageUrl && hasCategory);
}

/**
 * The full index wins whenever it provides a field. The exact Home preview
 * fills only its absent presentation values while that index refresh is still
 * partial, so asynchronous warming cannot erase the subtitle/badge the user
 * just saw on the same Topic card.
 */
function preserveNavigationPreview(topic: PanelTopic | undefined, preview: TopicRecord | undefined): PanelTopic | undefined {
	if (!topic || !preview) return topic ?? preview;
	return {
		...topic,
		title: topic.title || preview.title,
		subtitle: topic.subtitle || preview.subtitle,
		category: topic.category || preview.category,
		classification: topic.classification ?? preview.classification,
		coverImageUrl: topic.coverImageUrl ?? preview.coverImageUrl,
		coverBadge: topic.coverBadge ?? preview.coverBadge,
		audioMinutes: topic.audioMinutes || preview.audioMinutes,
		engagement: topic.engagement ?? preview.engagement,
	};
}

function record(value: unknown): Record<string, unknown> | undefined {
	return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : undefined;
}

function text(value: unknown): string | undefined {
	return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function number(value: unknown): number | undefined {
	return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : undefined;
}

function readActiveChapter(root: Root): ActiveChapterStamp | undefined {
	const holder = root as unknown as { __nowPlayingChapter?: ActiveChapterStamp; __activeChapter?: ActiveChapterStamp };
	return holder.__nowPlayingChapter ?? holder.__activeChapter;
}

function paintTopicCard(root: Root, active: ActiveChapterStamp | undefined, topic?: PanelTopic): void {
	const card = resolveElement(root, 'nowPlayingTopicCard');
	const topicId = active?.topicSlug;
	if (!card || !topicId) return;
	const value = topic;
	const favorite = Array.from(root.querySelectorAll<HTMLElement>('.fav-btn')).find((button) => button.dataset.topicId === topicId);
	const like = Array.from(root.querySelectorAll<HTMLElement>('.like-btn')).find((button) => button.dataset.topicId === topicId);
	const dislike = Array.from(root.querySelectorAll<HTMLElement>('.dislike-btn')).find((button) => button.dataset.topicId === topicId);
	const voteValue = like?.getAttribute('aria-pressed') === 'true' ? 1 : dislike?.getAttribute('aria-pressed') === 'true' ? -1 : 0;
	const isFavorite = favorite?.getAttribute('aria-pressed') === 'true' || favorite?.classList.contains('active') === true;
	const categories = topicCategoryLabels(value?.category, value?.classification);
	const { title, subtitle } = resolveNowPlayingTopicText(active, value);
	card.classList.add('topic-card', 'wp-topic-card', 'topic-poster-surface', 'topic-poster-surface--cover');
	card.dataset.id = topicId;
	card.dataset.protoClick = `showTopicDetail('${topicId}')`;
	card.tabIndex = 0;
	card.setAttribute('role', 'link');
	card.setAttribute('aria-label', `Open topic: ${title}`);
	card.innerHTML = topicPosterSurfaceMarkup({
		poster: {
			title,
			subtitle,
			categoryLabels: categories,
			chapterCount: panelChapterCount(value),
			audioMinutes: value?.audioMinutes,
			coverBadge: value?.coverBadge ?? (value?.source === 'github_trending' ? 'github' : undefined),
			topicId,
			voteValue,
			isFavorite,
			titleTag: 'h4',
			subtitleClass: 'topic-keywords',
			metricVariant: 'topic-card',
			categoryVariant: 'topic-card',
			ids: {
				metrics: 'nowPlayingTopicMetrics',
				like: 'nowPlayingLikeBtn',
				dislike: 'nowPlayingDislikeBtn',
				favorite: 'nowPlayingFavBtn',
			},
		},
		coverId: 'nowPlayingCover',
		coverClass: `topic-cover ${value?.coverGradient || 'grad-ai'} topic-cover--default`,
		contentClass: 'topic-body',
		footerClass: 'topic-footer',
	});
	const cover = card.querySelector<HTMLElement>('#nowPlayingCover');
	if (!cover) return;
	void applyTopicCover(cover, value?.coverImageUrl ?? coverUrlForTopic(categoriesFromClassification(value?.classification)));
	loadAuthoritativeReactions(root, card, topicId);
}

function panelChapterCount(topic: PanelTopic | undefined): number {
	const total = topic?.engagement?.chapters;
	if (typeof total === 'number' && Number.isFinite(total) && total >= 0) return total;
	return topic ? topic.previewChapters.length + topic.lockedChapters.length : 0;
}

function coverBadge(value: unknown): PanelTopic['coverBadge'] {
	return value === 'github' || value === 'new' || value === 'trending' ? value : undefined;
}

function loadAuthoritativeReactions(root: Root, card: HTMLElement, topicId: string): void {
	const runtime = getRuntime(root);
	if (!runtime?.api.listFavorites || !runtime.api.listTopicVotes) return;
	const scope = root as unknown as object;
	const sequence = (reactionLoadSequence.get(scope) ?? 0) + 1;
	reactionLoadSequence.set(scope, sequence);
	const favoriteVersionAtRead = topicFavoriteVersion(root, topicId);
	const voteVersionAtRead = topicVoteVersion(root, topicId);
	card.querySelectorAll<HTMLButtonElement>('.topic-actions .action-btn').forEach((button) => {
		button.disabled = true;
		button.setAttribute('aria-busy', 'true');
		button.dataset.topicReactionReadId = String(sequence);
	});
	void Promise.all([runtime.api.listFavorites(), runtime.api.listTopicVotes()]).then(([favorites, votes]) => {
		if (
			reactionLoadSequence.get(scope) !== sequence ||
			readActiveChapter(root)?.topicSlug !== topicId ||
			topicFavoriteVersion(root, topicId) !== favoriteVersionAtRead ||
			topicVoteVersion(root, topicId) !== voteVersionAtRead
		) return;
		// Writes disable every same-topic reaction control without aria-busy;
		// the initial authority fetch disables only this card with aria-busy.
		// This distinction keeps a late list response from erasing an optimistic
		// save/vote before its transaction commits or rolls back.
		const hasPendingWrite = (selector: string) => Array.from(root.querySelectorAll<HTMLButtonElement>(selector)).some((button) =>
			button.dataset.topicId === topicId && button.disabled && button.getAttribute('aria-busy') !== 'true',
		);
		const favoriteWritePending = hasPendingWrite('.fav-btn[data-topic-id]');
		const voteWritePending = hasPendingWrite('.like-btn[data-topic-id], .dislike-btn[data-topic-id]');
		const favoriteItems = (favorites as { favorites?: Array<{ topicId?: string; topicSlug?: string }> }).favorites ?? [];
		const favorite = favoriteItems.some((item) => item.topicId === topicId || item.topicSlug === topicId);
		const vote = ((votes as { votes?: Array<{ topicId?: string; voteValue?: number }> }).votes ?? []).find((item) => item.topicId === topicId)?.voteValue ?? 0;
		for (const button of Array.from(card.querySelectorAll<HTMLButtonElement>('.topic-actions .action-btn'))) {
			// Do not let a late read overwrite a user gesture that is still in
			// flight. learningActions marks the originating live control and
			// synchronizes every same-topic peer optimistically.
			if (button.dataset.favoritePending === 'true') continue;
			if (button.classList.contains('fav-btn') && !favoriteWritePending) button.classList.toggle('active', favorite);
			if (button.classList.contains('like-btn') && !voteWritePending) button.classList.toggle('active', vote === 1);
			if (button.classList.contains('dislike-btn') && !voteWritePending) button.classList.toggle('active', vote === -1);
			button.setAttribute('aria-pressed', String(button.classList.contains('active')));
			const pending = button.classList.contains('fav-btn') ? favoriteWritePending : voteWritePending;
			button.disabled = pending;
			button.removeAttribute('aria-busy');
		}
	}).catch(() => undefined).finally(() => {
		// A version-fenced stale read intentionally does not paint values, but it
		// still owns the card's initial aria-busy state. Release only this read's
		// buttons; a real write remains disabled through its own pending fence.
		releaseReactionReadLoading(root, card, topicId, sequence);
	});
}

function releaseReactionReadLoading(root: Root, card: HTMLElement, topicId: string, sequence: number): void {
	if (resolveElement(root, 'nowPlayingTopicCard') !== card || readActiveChapter(root)?.topicSlug !== topicId) return;
	const writePending = (selector: string) => Array.from(root.querySelectorAll<HTMLButtonElement>(selector)).some((button) =>
		button.dataset.topicId === topicId && button.disabled && button.getAttribute('aria-busy') !== 'true',
	);
	const favoriteWritePending = writePending('.fav-btn[data-topic-id]');
	const voteWritePending = writePending('.like-btn[data-topic-id], .dislike-btn[data-topic-id]');
	card.querySelectorAll<HTMLButtonElement>(`.topic-actions .action-btn[data-topic-reaction-read-id="${sequence}"]`).forEach((button) => {
		button.removeAttribute('aria-busy');
		button.disabled = button.classList.contains('fav-btn') ? favoriteWritePending : voteWritePending;
	});
}

async function renderPlaylist(root: Root, active: ActiveChapterStamp | undefined): Promise<void> {
	const container = resolveElement(root, 'nowPlayingPlaylist');
	if (!container) return;
	bindPlaylistClicks(root, container);

	const topicId = active?.topicSlug ?? getRuntime(root)?.activeCourseId;
	if (!topicId) return;

	const cacheKey = `${topicId}|${viewerCacheScope(root)}`;
	const cached = playlistCache.get(cacheKey);
	if (cached) {
		paintPlaylistRows(root, container, topicId, cached, active?.index);
		return;
	}
	const api = getRuntime(root)?.api;
	if (!api) return;
	try {
		const list = await api.listTopicChapters(topicId);
		const chapters = Array.isArray(list?.chapters) ? list.chapters : [];
		if (!chapters.length) return;
		playlistCache.set(cacheKey, chapters);
		// The user may have switched topics/chapters while the list loaded.
		const latest = readActiveChapter(root);
		if ((latest?.topicSlug ?? getRuntime(root)?.activeCourseId) !== topicId) return;
		paintPlaylistRows(root, container, topicId, chapters, latest?.index);
	} catch {
		// Playlist is an enhancement; the panel stays usable without it.
	}
}

/** Cache rows per viewer scope so lock markers cannot go stale across a
 * login/logout or plan upgrade within one session. */
function viewerCacheScope(root: Root): string {
	const state = (root as unknown as { __prototypeState?: { isLoggedIn?: boolean; effectiveAccessPlan?: string } }).__prototypeState;
	if (!state) return 'unknown';
	return state.isLoggedIn ? `auth-${state.effectiveAccessPlan ?? ''}` : 'guest';
}

function paintPlaylistRows(root: Root, container: HTMLElement, topicId: string, chapters: TopicChapterListItem[], activeIndex: number | undefined): void {
	container.replaceChildren(...chapters.map((chapter) => buildPlaylistRow(root, topicId, chapter, chapter.index === activeIndex)));
	const metrics = resolveElement(root, 'nowPlayingTopicMetrics');
	if (metrics) {
		const minutes = chapters.reduce((total, chapter) => total + Number(chapter.duration?.match(/\d+/)?.[0] ?? 0), 0);
		paintTopicCardMetrics(metrics, chapters.length, minutes);
	}
}

/** The header is built by buildTopicCard(), including its shared book/clock
 * icons. Update only the two value spans after playlist hydration so the
 * Now Playing poster remains the same card rather than a text-only fork. */
function paintTopicCardMetrics(metrics: HTMLElement, chapterCount: number, minutes: number): void {
	const values = Array.from(metrics.querySelectorAll<HTMLSpanElement>(':scope > span'));
	if (values.length >= 2) {
		values[0]!.textContent = `${chapterCount} chapter${chapterCount === 1 ? '' : 's'}`;
		values[1]!.textContent = minutes ? `${minutes} min` : '-';
		return;
	}
	// Defensive legacy-card fallback. Fresh panels always take the shared DOM
	// branch above, which preserves the For You SVG metrics.
	metrics.textContent = [chapterCount ? `${chapterCount} chapters` : '', minutes ? `${minutes} min` : ''].filter(Boolean).join(' · ');
}

function buildPlaylistRow(root: Root, topicId: string, chapter: TopicChapterListItem, isActive: boolean): HTMLButtonElement {
	const doc = document;
	const row = doc.createElement('button');
	row.type = 'button';
	row.className = 'wp-playlist-row';
	row.dataset.chapterIndex = String(chapter.index);
	row.setAttribute('role', 'listitem');
	const locked = !chapterPlayable(root, topicId, chapter.index);
	if (locked) {
		row.classList.add('locked');
		row.setAttribute('aria-label', `Chapter ${chapter.index} is locked: ${chapter.title}`);
	} else {
		row.setAttribute('aria-label', `Play chapter ${chapter.index}: ${chapter.title}`);
	}
	if (isActive) row.classList.add('active');

	const indicator = doc.createElement('span');
	indicator.className = 'wp-playlist-indicator';
	indicator.setAttribute('aria-hidden', 'true');
	const eq = doc.createElement('span');
	eq.className = 'wp-eq';
	eq.innerHTML = '<i></i><i></i><i></i><i></i>';
	const index = doc.createElement('span');
	index.className = 'wp-playlist-index';
	index.textContent = String(chapter.index);
	// Side-by-side: EQ on the LEFT, chapter index on the RIGHT. The active
	// row hides the index because the EQ takes the slot at the same position,
	// so the row's vertical rhythm stays constant across rows.
	indicator.appendChild(eq);
	indicator.appendChild(index);

	const title = doc.createElement('span');
	title.className = 'wp-playlist-title';
	title.textContent = chapter.title;

	const duration = doc.createElement('span');
	duration.className = 'wp-playlist-duration';
	if (locked) {
		// Locked chapters swap the duration for a lock marker - the time a
		// paid chapter would take is still shown on its (partial) article page.
		duration.classList.add('wp-playlist-lock');
		duration.setAttribute('title', 'Locked chapter');
		duration.innerHTML = LOCK_ICON_SVG;
	} else {
		duration.textContent = chapter.duration || '';
	}

	row.appendChild(indicator);
	row.appendChild(title);
	row.appendChild(duration);
	return row;
}

/** Client L1 pre-check: does the current viewer have media access to this
 * chapter? Mirrors the server's media.audio.play policy (free_chapters
 * allowance). Server remains the source of truth on play. */
function chapterPlayable(root: Root, topicId: string, chapterIndex: number): boolean {
	return resolveMediaAccessDecision(root, 'audio', topicId, chapterIndex).kind === 'allow';
}

function bindPlaylistClicks(root: Root, container: HTMLElement): void {
	if (clickBoundRoots.has(container)) return;
	clickBoundRoots.add(container);
	container.addEventListener('click', (event) => {
		const target = event.target instanceof Element ? event.target : null;
		const row = target?.closest<HTMLElement>('.wp-playlist-row');
		const index = Number(row?.dataset.chapterIndex);
		if (!row || !Number.isInteger(index) || index < 1) return;
		// L1 gate before navigation: a locked chapter must present the
		// paywall/login wall directly instead of running the doomed token
		// fetch and leaving the player on a denied chapter's identity.
		const topicId = readActiveChapter(root)?.topicSlug ?? getRuntime(root)?.activeCourseId;
		if (topicId && !chapterPlayable(root, topicId, index)) {
			const decision = resolveMediaAccessDecision(root, 'audio', topicId, index);
			presentMediaAccessDecision(root, decision, 'playlist-chapter');
			return;
		}
		root.dispatchEvent(new CustomEvent('deepmox:audio-goto', { detail: { index } }));
	});
}

function resolveElement(root: Root, id: string): HTMLElement | null {
	const doc = root instanceof Document ? root : root.ownerDocument ?? document;
	return doc.getElementById(id);
}
